import { useState, useEffect } from 'react'
import { useParams, Link, useOutletContext } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { 
  Activity, 
  Radio, 
  TrendingUp, 
  Box, 
  Code2, 
  ArrowRight, 
  Sparkles, 
  MessageSquare 
} from 'lucide-react'
import { getFirebaseDb } from '@/lib/firebase'
import { collection, query, getDocs, orderBy, limit, doc, onSnapshot } from 'firebase/firestore'
import { OrganizationContext } from './AssistPage'

interface SignalStats {
  totalSignals: number
  last24Hours: number
  last7Days: number
}

interface SignalModel {
  id: string
  slug?: string
  title?: string
  name?: string  // Admin API uses 'name' instead of 'title'
  description?: string
  requiredFields?: string[]  // UI format
  fields?: { name: string; type: string }[]  // Admin API format
  status?: 'configured' | 'discovered'
  isActive?: boolean  // Admin API format
  createdAt: Date
}

// Helper to get display name
function getModelDisplayName(model: SignalModel): string {
  return model.title || model.name || 'Untitled'
}

// Helper to get field count
function getModelFieldCount(model: SignalModel): number {
  if (model.fields && Array.isArray(model.fields)) {
    return model.fields.length
  }
  if (model.requiredFields && Array.isArray(model.requiredFields)) {
    return model.requiredFields.length
  }
  return 0
}

export function SignalsPage() {
  const { orgId } = useParams<{ orgId: string }>()
  const orgContext = useOutletContext<OrganizationContext>()
  const [stats, setStats] = useState<SignalStats>({ totalSignals: 0, last24Hours: 0, last7Days: 0 })
  const [models, setModels] = useState<SignalModel[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const organizationId = orgContext?.organizationId
    if (!organizationId) {
      setLoading(false)
      return
    }
    
    let unsubscribe: (() => void) | undefined
    
    const setupListener = async () => {
      try {
        const db = getFirebaseDb()
        
        // Fetch signal models from organization subcollection (one-time)
        const modelsRef = collection(db, 'organizations', organizationId, 'signalModels')
        const modelsQuery = query(modelsRef, orderBy('createdAt', 'desc'), limit(5))
        const modelsSnapshot = await getDocs(modelsQuery)
        
        const modelsData = modelsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() || new Date()
        })) as SignalModel[]
        
        setModels(modelsData)
        
        // Set up real-time listener for statistics
        const statsRef = doc(db, 'organizations', organizationId, 'statistics', 'totals')
        unsubscribe = onSnapshot(statsRef, (statsDoc) => {
          if (statsDoc.exists()) {
            const statsData = statsDoc.data()
            setStats({
              totalSignals: statsData.totalSignals || 0,
              last24Hours: statsData.last24Hours || 0,
              last7Days: statsData.last7Days || 0
            })
          } else {
            setStats({
              totalSignals: 0,
              last24Hours: 0,
              last7Days: 0
            })
          }
          setLoading(false)
        }, (error) => {
          console.error('Error listening to stats:', error)
          setLoading(false)
        })
      } catch (error) {
        console.error('Error fetching signals data:', error)
        setLoading(false)
      }
    }
    
    setupListener()
    
    return () => {
      if (unsubscribe) unsubscribe()
    }
  }, [orgContext?.organizationId])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-3">
        <Activity className="h-8 w-8" />
        <div>
          <h1 className="text-3xl font-bold">Signals</h1>
          <p className="text-muted-foreground">
            Real-time event streaming and intelligent data processing
          </p>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Signals</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalSignals.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">All time</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Last 24 Hours</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.last24Hours.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">Events tracked</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Last 7 Days</CardTitle>
            <Radio className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.last7Days.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">Events tracked</p>
          </CardContent>
        </Card>
      </div>

      {/* Data Flow Visualization */}
      <Card>
        <CardHeader>
          <CardTitle>How Signals Work</CardTitle>
          <CardDescription>Real-time data streaming from your application to Session</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg bg-gradient-to-br from-primary/5 via-primary/10 to-primary/5 p-8 border">
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1 text-center space-y-2">
                <div className="mx-auto w-16 h-16 rounded-lg bg-background border-2 border-primary/20 flex items-center justify-center">
                  <Code2 className="h-8 w-8 text-primary" />
                </div>
                <div className="text-sm font-semibold">Your App</div>
                <div className="text-xs text-muted-foreground">User interactions<br/>Product events</div>
              </div>
              
              <div className="flex flex-col items-center gap-2">
                <ArrowRight className="h-6 w-6 text-primary" />
                <div className="text-xs font-medium text-primary">Stream</div>
              </div>

              <div className="flex-1 text-center space-y-2">
                <div className="mx-auto w-16 h-16 rounded-lg bg-background border-2 border-primary/20 flex items-center justify-center">
                  <Radio className="h-8 w-8 text-primary animate-pulse" />
                </div>
                <div className="text-sm font-semibold">Signals SDK</div>
                <div className="text-xs text-muted-foreground">Batch & buffer<br/>Real-time transmission</div>
              </div>

              <div className="flex flex-col items-center gap-2">
                <ArrowRight className="h-6 w-6 text-primary" />
                <div className="text-xs font-medium text-primary">Process</div>
              </div>

              <div className="flex-1 text-center space-y-2">
                <div className="mx-auto w-16 h-16 rounded-lg bg-background border-2 border-primary/20 flex items-center justify-center">
                  <Activity className="h-8 w-8 text-primary" />
                </div>
                <div className="text-sm font-semibold">Session Platform</div>
                <div className="text-xs text-muted-foreground">Index & analyze<br/>Power features</div>
              </div>
            </div>
          </div>

          {/* Use Cases */}
          <div className="mt-6 space-y-4">
            <h4 className="font-semibold">Powered Features</h4>
            <div className="grid gap-3 md:grid-cols-3">
              <div className="flex items-start space-x-3 rounded-lg border p-3">
                <Sparkles className="h-5 w-5 mt-0.5 text-primary flex-shrink-0" />
                <div>
                  <h5 className="font-semibold text-sm">Recommendations</h5>
                  <p className="text-xs text-muted-foreground mt-1">
                    Train collaborative filtering models with interaction signals
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3 rounded-lg border p-3">
                <TrendingUp className="h-5 w-5 mt-0.5 text-primary flex-shrink-0" />
                <div>
                  <h5 className="font-semibold text-sm">Analytics</h5>
                  <p className="text-xs text-muted-foreground mt-1">
                    Track behavior, conversions, and business intelligence
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3 rounded-lg border p-3">
                <MessageSquare className="h-5 w-5 mt-0.5 text-primary flex-shrink-0" />
                <div>
                  <h5 className="font-semibold text-sm">AI Assistant</h5>
                  <p className="text-xs text-muted-foreground mt-1">
                    Provide context-aware customer support
                  </p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Models and SDK Cards Side by Side */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Models Card */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <div className="flex items-center space-x-2">
                <Box className="h-5 w-5" />
                <CardTitle>Signal Models</CardTitle>
              </div>
              <CardDescription>
                Define indexed object types
              </CardDescription>
            </div>
            <Link to={`/${orgId}/assist/signals/models`}>
              <Button variant="outline" size="sm">
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {models.length === 0 ? (
              <div className="rounded-lg border border-dashed p-6 text-center">
                <Box className="mx-auto h-10 w-10 text-muted-foreground" />
                <h3 className="mt-3 text-base font-semibold">No Models</h3>
                <p className="mt-2 text-xs text-muted-foreground">
                  Define your first signal model to start tracking events
                </p>
                <Link to={`/${orgId}/assist/signals/models`}>
                  <Button className="mt-4" size="sm">
                    Create Model
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {models.slice(0, 3).map((model) => (
                  <div key={model.id} className="flex items-center justify-between rounded-lg border p-3">
                    <div>
                      <div className="font-medium text-sm">{getModelDisplayName(model)}</div>
                      <div className="text-xs text-muted-foreground">
                        {getModelFieldCount(model)} fields
                      </div>
                    </div>
                    <Box className="h-4 w-4 text-muted-foreground" />
                  </div>
                ))}
                {models.length > 3 && (
                  <Link to={`/${orgId}/assist/signals/models`} className="block text-center text-sm text-primary hover:underline">
                    View all {models.length} models
                  </Link>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* SDK Integration Card */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <div className="flex items-center space-x-2">
                <Code2 className="h-5 w-5" />
                <CardTitle>SDK Integration</CardTitle>
              </div>
              <CardDescription>
                Get started with code snippets
              </CardDescription>
            </div>
            <Link to={`/${orgId}/assist/signals/code`}>
              <Button variant="outline" size="sm">
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="rounded-lg bg-muted p-4">
                <pre className="text-xs overflow-x-auto">
                  <code>{`import { Signals } from '@logg/signals'

const signals = new Signals({
  apiKey: 'YOUR_API_KEY',
  endpoint: 'YOUR_ENDPOINT'
})

// Track an event
signals.event({
  type: 'page_view',
  url: window.location.href,
  title: document.title
})`}</code>
                </pre>
              </div>
              <Link to={`/${orgId}/assist/signals/code`}>
                <Button variant="outline" className="w-full" size="sm">
                  View Full Documentation
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
